import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import {
  OutAdminProductExtendedViewDTO,
  OutAdminProductExtendedViewDTODescriptionSkipReasonEnum,
  OutAdminProductExtendedViewDTODescriptionSourceEnum,
  RegeneratedDescriptionDTO
} from "@app/api/generated";
import { ClickableComponent } from "@app/core";
import { ConfirmModal } from "@app/components/confirm-modal/confirm-modal";
import { regenerateForProduct } from "@app/api/product-description-api";
import { formatConfidence } from "@app/util/format-confidence";
import { useDateFnsFormat } from "@app/util/use-date-fns-format";

import styles from "./product-detail-sidebar.module.scss";

interface IDescriptionPanelProps {
  product: OutAdminProductExtendedViewDTO;
  /**
   * True while the form has unsaved edits. Generating reloads the product and remounts the form, so
   * those edits would be lost without a word — the button waits until they are saved.
   */
  hasUnsavedChanges: boolean;
  /** Called after a run so the page reloads the product and the editor shows the new text. */
  onGenerated: () => Promise<void>;
}

const SKIP_REASON_KEY: Record<OutAdminProductExtendedViewDTODescriptionSkipReasonEnum, string> = {
  LOW_CONFIDENCE: "products.detail.description.skip.lowConfidence",
  BAD_LENGTH: "products.detail.description.skip.badLength",
  API_ERROR: "products.detail.description.skip.apiError"
};

const DescriptionPanel = ({ product, hasUnsavedChanges, onGenerated }: IDescriptionPanelProps): JSX.Element => {
  const intl = useIntl();
  const { formatDate } = useDateFnsFormat();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // `generate` closes over the value at click time, so read the live one through a ref.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const isManual = product.descriptionSource === OutAdminProductExtendedViewDTODescriptionSourceEnum.MANUAL;
  const hasEan = Boolean(product.ean?.trim());

  const sourceText = (): string => {
    if (!product.descriptionSource) {
      return "—";
    }
    return intl.formatMessage({
      id: isManual ? "products.detail.description.source.manual" : "products.detail.description.source.ai"
    });
  };

  /** An unaccepted result is normal ("rather empty than invented"), so it is a message, not an error. */
  const reportResult = (result: RegeneratedDescriptionDTO) => {
    if (result.accepted) {
      toast.success(intl.formatMessage({ id: "products.detail.description.toast.generated" }));
      return;
    }
    // A failed call comes with the reason from the ESB ("Gemini answered 429 — …"); that says more
    // than the label "call failed", so it wins when it is there.
    const reason =
      result.failureMessage ||
      (result.skipReason
        ? intl.formatMessage({ id: SKIP_REASON_KEY[result.skipReason] })
        : intl.formatMessage({ id: "products.detail.description.skip.unknown" }));
    toast.info(intl.formatMessage({ id: "products.detail.description.toast.skipped" }, { reason }));
  };

  const generate = async (force: boolean) => {
    setIsGenerating(true);
    try {
      reportResult(await regenerateForProduct(product.id, force));

      // The guard on the button only covers the moment of the click. Generating takes seconds, and
      // whoever starts typing in that window would lose it: reloading remounts the form. Their edits
      // win — the new text is stored, so a reload of the page shows it whenever they are ready.
      if (hasUnsavedChangesRef.current) {
        toast.info(intl.formatMessage({ id: "products.detail.description.toast.reloadPending" }));
        return;
      }
      await onGenerated();
    } catch {
      toast.error(intl.formatMessage({ id: "products.detail.description.toast.error" }));
    } finally {
      setIsGenerating(false);
    }
  };

  const onGenerateClick = () => {
    if (isManual) {
      setIsConfirmOpen(true);
      return;
    }
    generate(false);
  };

  return (
    <>
      <h3 className={styles.heading}>{intl.formatMessage({ id: "products.detail.description.heading" })}</h3>
      <dl className={styles.meta}>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.description.writtenBy" })}</dt>
          <dd>{sourceText()}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.description.generatedAt" })}</dt>
          <dd>
            {product.descriptionGeneratedAt
              ? formatDate(new Date(product.descriptionGeneratedAt), "dd-MM-yyyy HH:mm")
              : "—"}
          </dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.description.confidence" })}</dt>
          <dd>
            {typeof product.descriptionConfidence === "number"
              ? formatConfidence(intl, product.descriptionConfidence)
              : "—"}
          </dd>
        </div>
        {product.descriptionSkipReason && (
          <div>
            <dt>{intl.formatMessage({ id: "products.detail.description.lastAttempt" })}</dt>
            <dd>{intl.formatMessage({ id: SKIP_REASON_KEY[product.descriptionSkipReason] })}</dd>
          </div>
        )}
      </dl>

      <div className={styles.action}>
        <ClickableComponent
          buttonType="button"
          title={intl.formatMessage({
            id: isGenerating ? "products.detail.description.generating" : "products.detail.description.generate"
          })}
          onClick={onGenerateClick}
          disabled={isGenerating || !hasEan || hasUnsavedChanges}
        />
        {!hasEan && (
          <p className={styles.hint}>{intl.formatMessage({ id: "products.detail.description.noEanHint" })}</p>
        )}
        {hasEan && hasUnsavedChanges && (
          <p className={styles.hint}>{intl.formatMessage({ id: "products.detail.description.unsavedHint" })}</p>
        )}
      </div>

      <ConfirmModal
        isOpen={isConfirmOpen}
        title={intl.formatMessage({ id: "products.detail.description.confirm.title" })}
        message={intl.formatMessage({ id: "products.detail.description.confirm.message" })}
        onConfirm={() => {
          setIsConfirmOpen(false);
          generate(true);
        }}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </>
  );
};

export { DescriptionPanel };
